import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiError, asApiError } from './api';

export interface Resource<T> {
  data: T | undefined;
  error: ApiError | undefined;
  /** True until the first answer arrives (or while the path changes). */
  loading: boolean;
  /** True while any request is in flight, including a background refresh. */
  refreshing: boolean;
  reload: () => Promise<void>;
  setData: (update: T | ((prev: T | undefined) => T)) => void;
}

/**
 * GET a path and keep it fresh. `interval` polls while the tab is visible;
 * a refresh keeps the previous data on screen (no flash), and a changed
 * path holds the old data until the new answer lands.
 */
export function useResource<T>(path: string | null, options: { interval?: number | false } = {}): Resource<T> {
  const { interval } = options;
  const [data, setDataState] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);
  const pathRef = useRef(path);
  pathRef.current = path;

  const load = useCallback(async () => {
    const current = pathRef.current;
    if (current === null) return;
    const mine = ++seq.current;
    setRefreshing(true);
    try {
      const answer = await api<T>(current);
      if (mine !== seq.current) return;
      setDataState(answer);
      setError(undefined);
    } catch (err) {
      if (mine !== seq.current) return;
      if ((err as Error).name === 'AbortError') return;
      setError(asApiError(err));
    } finally {
      if (mine === seq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void load();
  }, [path, load]);

  useEffect(() => {
    if (!interval || path === null) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, interval);
    return () => window.clearInterval(id);
  }, [interval, path, load]);

  const setData = useCallback((update: T | ((prev: T | undefined) => T)) => {
    setDataState((prev) => (typeof update === 'function' ? (update as (p: T | undefined) => T)(prev) : update));
  }, []);

  return { data, error, loading, refreshing, reload: load, setData };
}

/** A ticking clock, so "4 min ago" and running durations stay current. */
export function useNow(every = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), every);
    return () => window.clearInterval(id);
  }, [every]);
  return now;
}

/** Copy text; `copied` flips true for a moment so the button can say so. */
export function useCopy(timeout = 1600): { copied: boolean; copy: (text: string) => Promise<void> } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Older browsers and insecure origins: fall back to a hidden textarea.
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        area.remove();
      }
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), timeout);
    },
    [timeout],
  );
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return { copied, copy };
}

/** Run an async action with busy and error state, for buttons and forms. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    setBusy(true);
    setError(undefined);
    try {
      return await fnRef.current(...args);
    } catch (err) {
      setError(asApiError(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { run, busy, error, setError };
}

export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} · offstage cloud` : 'offstage cloud';
  }, [title]);
}
